import {SectionItem} from 'common/components/items-menu/types';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {ChartVizType} from 'common/report/constants';

export const SEGMENT_SEPARATOR = ` / `;
export const OTHER_PROP = `$other`;
export const OVERALL_PROP = `$overall`;
export const OVERALL_PROP_NAME = `Overall`;
export const CONVERSION_WINDOW_KEY = `conversionwindow`;

const CONVERSION_HELP_LINK = `\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/funnels#counting-method)`;
const commonTooltipAttrs = {size: DisplaySize.Small};

export enum FunnelOrder {
  Loose = `loose`,
  Any = `any`,
}

export enum FunnelsCountType {
  Unique = `unique`,
  General = `general`,
  Session = `session`,
  ConversionRate = `conversion_rate`,
  TimeToConvert = `time_to_convert`,
  PropertySum = `total`,
}

export const COUNT_TYPE_OPTIONS: SectionItem[] = [
  {
    label: `Uniques`,
    value: FunnelsCountType.Unique,
    tooltip: {text: `Count a single entry per user.${CONVERSION_HELP_LINK}`, ...commonTooltipAttrs},
  },
  {
    label: `Totals`,
    value: FunnelsCountType.General,
    tooltip: {
      text: `Count multiple entries per user. User may re-enter after converting or dropping off.${CONVERSION_HELP_LINK}`,
      ...commonTooltipAttrs,
    },
  },
  {
    label: `Sessions`,
    value: FunnelsCountType.Session,
    tooltip: {
      text: `Count the number of sessions that convert.${CONVERSION_HELP_LINK}`,
      ...commonTooltipAttrs,
    },
  },
];

export const ORDER_OPTIONS: SectionItem[] = [
  {
    label: `specific order`,
    value: FunnelOrder.Loose,
    tooltip: {
      text: `Users must complete the funnel in step order to convert.${CONVERSION_HELP_LINK}`,
      ...commonTooltipAttrs,
    },
  },
  {
    label: `any order`,
    value: FunnelOrder.Any,
    tooltip: {
      text: `Users can complete unanchored funnels steps in any order to convert.${CONVERSION_HELP_LINK}`,
      ...commonTooltipAttrs,
    },
  },
];

export const CONVERSION_WINDOW_TOOLTIP = {
  text: `The time window a user has to complete a funnel from the time they enter.${CONVERSION_HELP_LINK}`,
  ...commonTooltipAttrs,
};

export const HOLD_PROPERTY_CONSTANT_TOOLTIP = {
  text: `A property that must retain a single value across all funnels steps for a user to convert through the funnel.`,
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const HOLD_PROPERTY_CONSTANT_DISABLED_TOOLTIP = {
  text: `A maximum of 3 properties can be held constant at the same time.`,
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const EXCLUSION_EVENTS_TOOLTIP = {
  text: `Exclude users in between steps who performed exclusion events.`,
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const STEPS_VIZ_TYPES = [ChartVizType.Steps, ChartVizType.Metric];
export const FREQUENCY_VIZ_TYPES = [ChartVizType.Bar, ChartVizType.Line];
export const TTC_MEDIAN_VIZ_TYPES = [ChartVizType.Bar, ChartVizType.Metric];
export const TTC_DISTRIBUTION_VIZ_TYPES = [ChartVizType.Bar, ChartVizType.Line];

export const EMPTY_BOOKMARK_NAME = `Untitled Funnels Report`;

export const ATTRIBUTION_OPTIONS: SectionItem[] = [
  {
    label: `Last touch`,
    value: `last_touch`,
    isSelected: true,
  },
  {
    label: `First touch`,
    value: `first_touch`,
    isSelected: false,
  },
];

const ATTRIBUTION_HELP_LINK = `\n[Learn more](https://docs.mixpanel.com/docs/analysis/reports/funnels#first-touch-vs-last-touch)`;
export const ATTRIBUTION_TYPE_TOOLTIPS: {[key: string]: any} = {
  [ATTRIBUTION_OPTIONS[0].value]: {
    text: `Use the property value from the last step reached to attribute conversion.${ATTRIBUTION_HELP_LINK}`,
    size: DisplaySize.Small,
  },
  [ATTRIBUTION_OPTIONS[1].value]: {
    text: `Use the property value from the first step it was seen to attribute conversion.${ATTRIBUTION_HELP_LINK}`,
    size: DisplaySize.Small,
  },
};

/** All metrics that can be shown in the table */
export enum TableToggleableMetrics {
  StatSig = `stat-sig`,
  ConvFirstStep = `conv-first-step`,
  ConvPrevStep = `conv-prev-step`,
  Count = `count`,
  TimeFirstStep = `time-first-step`,
  TimePrevStep = `time-prev-step`,
}

export const TABLE_COLUMN_SELECTOR_DISPLAY_NAME: {[key in TableToggleableMetrics]: string} = {
  [TableToggleableMetrics.StatSig]: `Statistical Significance`,
  [TableToggleableMetrics.Count]: `Step Count`,
  [TableToggleableMetrics.TimePrevStep]: `Average time from previous step`,
  [TableToggleableMetrics.TimeFirstStep]: `Average time from first step`,
  [TableToggleableMetrics.ConvPrevStep]: `Conversion from previous step`,
  [TableToggleableMetrics.ConvFirstStep]: `Conversion from first step`,
};

export const TABLE_COLUMN_SELECTOR_TOOLTIPS: {[key in TableToggleableMetrics]: string} = {
  [TableToggleableMetrics.StatSig]: `Display if a segment has a statistically significant difference compared to overall or any other segment.`,
  [TableToggleableMetrics.Count]: `Display the absolute number of conversions at a particular step.`,
  [TableToggleableMetrics.TimePrevStep]: `Display the average time from the previous step, for users who converted.`,
  [TableToggleableMetrics.TimeFirstStep]: `Display the average time from the first step, for users who converted.`,
  [TableToggleableMetrics.ConvPrevStep]: `Display the percent that reached a step from the prior step.`,
  [TableToggleableMetrics.ConvFirstStep]: `Display the percent that reached a step from the first step.`,
};

export enum ConversionWindowUnit {
  Second = `second`,
  Minute = `minute`,
  Hour = `hour`,
  Day = `day`,
  Week = `week`,
  Month = `month`,
  Session = `session`,
}
